import { motion } from 'framer-motion'
import { CheckCircle, Users, Calendar, Award } from 'lucide-react'
import SectionTitle from './SectionTitle.tsx'

const About = () => {
  const services = [
    'Web Design & Development',
    'Application Development',
    'Creative Direction',
    'UX/UI Design',
    'Content & Resource Management',
    'Digital Strategy & Ongoing Support'
  ]

  const stats = [
    { icon: Calendar, label: 'Years Experience', value: '17+' },
    { icon: Users, label: 'Projects Completed', value: '100+' },
    { icon: Award, label: 'Awards Won', value: '15+' },
  ]

  return (
    <section id="about" className="section-padding">
      <div className="container">
        <SectionTitle
          subtitle="About"
          title="Real Design. Real Results."
          description="We craft tailored digital experiences that connect with your audience, both internal and external. Every project is built with a focus on precision, creativity, and measurable results."
        />

        <div className="mt-16 lg:mt-20">
          {/* Unified Container with Background Image */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative rounded-[30px] overflow-hidden min-h-[600px] lg:min-h-[700px]"
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <img
                src="/assets/images/laptop-ss.webp"
                alt="Our creative team at work"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/80 to-primary/70"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 p-8 lg:p-16 text-white">
              <div className="max-w-4xl">
                <h3 className="text-3xl lg:text-3xl font-display font-bold mb-6">
                  Bringing Bold Ideas to Life
                </h3>
                
                <p className="text-lg lg:text-lg mb-12 leading-relaxed text-white/90 max-w-4xl">
                  At Alt Dev Studios, we believe design is more than visuals—it's about creating tools that empower 
                  first responders, healthcare teams, and the communities they serve. By blending strategy, creativity, 
                  and technical expertise, we deliver solutions that are bold, reliable, and built to make a real difference.
                </p>

                {/* Services Grid */}
                <div className="mb-12">
                  <h4 className="text-xl font-display font-bold mb-6 text-accent">Our Services</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {services.map((service, index) => (
                      <motion.div
                        key={service}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="flex items-center text-white"
                      >
                        <CheckCircle size={20} className="text-accent mr-4 flex-shrink-0" />
                        <span className="font-medium">{service}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-8 lg:gap-12">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="text-center"
                    >
                      <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/20 backdrop-blur-sm rounded-xl mb-3">
                        <stat.icon size={28} className="text-accent" />
                      </div>
                      <div className="text-3xl lg:text-4xl font-display font-bold text-white mb-2">
                        {stat.value}
                      </div>
                      <div className="text-sm text-white/80 font-medium">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default About
